import { Component, ViewChild, OnInit, OnDestroy, AfterViewInit, Inject, PLATFORM_ID } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute, RouterOutlet } from '@angular/router';
import { DOCUMENT, isPlatformBrowser, NgIf } from '@angular/common';
import { Title, Meta } from '@angular/platform-browser';
import { filter, map, mergeMap } from 'rxjs/operators';
import { ToastContainerComponent } from './shared/toast/toast-container.component';
import { FooterComponent } from './components/footer/footer.component';
import { NavigationComponent } from './components/navigation/navigation.component';
import { LoaderComponent } from './shared/loader/loader.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [NgIf, NavigationComponent, RouterOutlet, FooterComponent, ToastContainerComponent, LoaderComponent]
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  title = 'Finspire Software Solutions';
  showNavFooter = true;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private metaService: Meta,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    // 1. Handle Navigation UI states & Canonical URL
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const hiddenRoutes = ['/login', '/signup', '/forgot-password', '/admin'];
        this.showNavFooter = !hiddenRoutes.some(route => event.urlAfterRedirects.includes(route));
        this.updateCanonicalUrl(event.urlAfterRedirects);
      }
    });

    // 2. Handle Global SEO Meta Tags from Router Data
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map(route => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      filter(route => route.outlet === 'primary'),
      mergeMap(route => route.data)
    ).subscribe(data => {
      if (data && data['title']) {
        this.titleService.setTitle(data['title']);
        this.metaService.updateTag({ property: 'og:title', content: data['title'] }, "property='og:title'");
        this.metaService.updateTag({ name: 'twitter:title', content: data['title'] });
      }
      if (data && data['description']) {
        this.metaService.updateTag({ name: 'description', content: data['description'] });
        this.metaService.updateTag({ property: 'og:description', content: data['description'] }, "property='og:description'");
        this.metaService.updateTag({ name: 'twitter:description', content: data['description'] });
      }
    });
  }

  private updateCanonicalUrl(url: string): void {
    // Strip query parameters and fragments for canonical
    const cleanUrl = url.split('?')[0].split('#')[0];
    // Ensure no trailing slash unless it's the root
    const formattedUrl = cleanUrl === '/' ? cleanUrl : cleanUrl.replace(/\/$/, '');
    const fullUrl = `https://finspire.lk${formattedUrl}`;

    let link: HTMLLinkElement | null = this.document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', fullUrl);
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      // Use requestIdleCallback to guarantee this heavy DOM operation doesn't block the Main Thread 
      // or interfere with Lighthouse's Time to Interactive (TTI) measurement.
      const runObserver = () => this.setupAccessibilityObserver();
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(runObserver, { timeout: 10000 });
      } else {
        setTimeout(runObserver, 8000);
      }
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const splash = this.document.getElementById('global-splash-screen');
      if (splash) {
        splash.style.opacity = '0';
        splash.style.visibility = 'hidden';
        setTimeout(() => splash.remove(), 500);
      }
    }
  }

  private setupAccessibilityObserver(): void {
    const fixElement = (element: HTMLElement) => {
      // 1. Fix tabindex > 0
      const tabIndexAttr = element.getAttribute('tabindex');
      if (tabIndexAttr) {
        const tabIndexVal = parseInt(tabIndexAttr, 10);
        if (!isNaN(tabIndexVal) && tabIndexVal > 0) {
          const tagName = element.tagName.toLowerCase();
          if (tagName === 'div' || tagName === 'span') {
            element.removeAttribute('tabindex');
          } else {
            element.setAttribute('tabindex', '0');
          }
        }
      }

      // 2. Fix buttons/interactive roles without accessible names
      const tagName = element.tagName.toLowerCase();
      const isButton = tagName === 'button' || element.getAttribute('role') === 'button';
      if (isButton) {
        const hasAriaLabel = element.hasAttribute('aria-label') || element.hasAttribute('attr.aria-label');
        const hasAriaLabelledBy = element.hasAttribute('aria-labelledby');
        const hasText = element.innerText && element.innerText.trim().length > 0;
        if (!hasAriaLabel && !hasAriaLabelledBy && !hasText) {
          element.setAttribute('aria-label', 'Interactive element');
        }
      }
    };

    // Initial pass on existing elements
    const allElements = this.document.querySelectorAll('*');
    allElements.forEach((el) => fixElement(el as HTMLElement));

    // Dynamic observer for dynamically added extension elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const el = node as HTMLElement;
              fixElement(el);
              const children = el.querySelectorAll('*');
              children.forEach((child) => fixElement(child as HTMLElement));
            }
          });
        } else if (mutation.type === 'attributes' && mutation.attributeName === 'tabindex') {
          const target = mutation.target as HTMLElement;
          fixElement(target);
        }
      });
    });

    observer.observe(this.document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['tabindex']
    });
  }

  ngOnDestroy() { }
}
